import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { DatePicker, Form, Input, Modal, message } from 'antd'
import dayjs from 'dayjs'

import {
    clearMainTaskSprintDetail,
    createMainTaskSprintThunk,
    fetchMainTaskSprintDetailThunk,
    loadDevelopmentSprintBoard,
    updateMainTaskSprintThunk,
} from '../developmentSprint.slice'
import { clearProjectSprintOptionsCache } from '../../taskRefactored/components/sprintSelector'

import styles from './CreateSprintModal.module.css'
import { showActionNotification } from '../../../components/notification'

const { RangePicker } = DatePicker

const defaultDateRange = () => {
    const start = dayjs().startOf('day')
    const end = start.add(14, 'day')
    return [start, end]
}

const formValuesFromSprintDoc = (doc, titleFallback = '') => {
    const raw = doc || {}
    const rawStart = raw.start ?? raw.startDate ?? null
    const rawEnd = raw.end ?? raw.endDate ?? null
    const startV =
        rawStart != null && rawStart !== ''
            ? dayjs(rawStart)
            : defaultDateRange()[0]
    const endV =
        rawEnd != null && rawEnd !== '' ? dayjs(rawEnd) : startV.add(14, 'day')
    return {
        title: String(raw.title ?? titleFallback ?? ''),
        dateRange: [startV, endV],
    }
}

const flagsFromSprintDoc = (doc) => ({
    isActive: !!doc?.isActive,
    isComplete: !!doc?.isComplete,
})

const CreateSprintModal = ({
    open,
    onCancel,
    projectId,
    mode = 'create',
    initialSprint = null,
    onSaved,
}) => {
    const dispatch = useDispatch()
    const [form] = Form.useForm()
    const [submitting, setSubmitting] = useState(false)
    const [detailLoading, setDetailLoading] = useState(false)
    const [editFlags, setEditFlags] = useState({
        isActive: false,
        isComplete: false,
    })

    useEffect(() => {
        if (!open) return
        form.resetFields()
        let cancelled = false

        if (mode !== 'edit' || !initialSprint?.id) {
            setDetailLoading(false)
            setEditFlags({ isActive: false, isComplete: false })
            form.setFieldsValue({
                title: '',
                dateRange: defaultDateRange(),
            })
            return () => {
                cancelled = true
            }
        }

        setDetailLoading(true)
        ;(async () => {
            try {
                const { doc } = await dispatch(
                    fetchMainTaskSprintDetailThunk({
                        sprintId: initialSprint.id,
                    })
                ).unwrap()
                if (cancelled) return
                form.setFieldsValue(
                    formValuesFromSprintDoc(doc, initialSprint.title)
                )
                setEditFlags(flagsFromSprintDoc(doc))
            } catch (err) {
                if (cancelled) return
                const raw = initialSprint?.raw || {}
                form.setFieldsValue(
                    formValuesFromSprintDoc(raw, initialSprint.title)
                )
                setEditFlags(flagsFromSprintDoc(raw))
                const msg =
                    typeof err === 'string'
                        ? err
                        : err?.message ||
                          err?.error ||
                          'Could not load sprint details; using board data.'
                message.warning(msg)
            } finally {
                if (!cancelled) setDetailLoading(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [open, form, mode, initialSprint, dispatch])

    const handleClose = () => {
        form.resetFields()
        dispatch(clearMainTaskSprintDetail())
        setEditFlags({ isActive: false, isComplete: false })
        onCancel?.()
    }

    const handleSubmit = async (values) => {
        if (!projectId) {
            showActionNotification({
                type: 'error',
                message: 'Missing project',
            })
            return
        }
        const title = (values.title || '').trim()
        if (!title) {
            message.warning('Please enter a sprint name')
            return
        }
        const range = values.dateRange
        if (!Array.isArray(range) || !range[0] || !range[1]) {
            message.warning('Please select sprint dates')
            return
        }
        if (range[1].isBefore(range[0], 'day')) {
            message.warning('End date must be on or after start date')
            return
        }
        const start = range[0].startOf('day').toISOString()
        const end = range[1].startOf('day').toISOString()
        setSubmitting(true)
        try {
            const payload =
                mode === 'edit' && initialSprint?.id
                    ? await dispatch(
                          updateMainTaskSprintThunk({
                              sprintId: initialSprint.id,
                              title,
                              start,
                              end,
                              isActive: editFlags.isActive,
                              isComplete: editFlags.isComplete,
                          })
                      ).unwrap()
                    : await dispatch(
                          createMainTaskSprintThunk({
                              projectId,
                              title,
                              start,
                              end,
                          })
                      ).unwrap()
            if (payload?.success === false) {
                showActionNotification({
                    type: 'error',
                    message:
                        payload?.message ||
                        (mode === 'edit'
                            ? 'Could not update sprint'
                            : 'Could not create sprint'),
                })
                return
            }
            showActionNotification({
                type: 'success',
                message:
                    payload?.message ||
                    (mode === 'edit' ? 'Sprint updated' : 'Sprint created'),
                subMessage: title,
                actionButtonText: 'Undo',
                notificationKey:
                    mode === 'edit' ? 'sprint-updated' : 'sprint-created',
            })
            handleClose()
            clearProjectSprintOptionsCache(projectId)
            if (onSaved) {
                onSaved()
            } else {
                dispatch(loadDevelopmentSprintBoard({ projectId }))
            }
        } catch (err) {
            const msg =
                typeof err === 'string'
                    ? err
                    : err?.message ||
                      err?.error ||
                      (mode === 'edit'
                          ? 'Could not update sprint'
                          : 'Could not create sprint')
            showActionNotification({
                type: 'error',
                message: msg,
            })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Modal
            className={styles.modal}
            title={
                <div className={styles.header}>
                    <h2 className={styles.modalTitle}>
                        {mode === 'edit' ? 'Edit sprint' : 'Create sprint'}
                    </h2>
                    <p className={styles.subtitle}>
                        {mode === 'edit'
                            ? 'Update sprint name and schedule.'
                            : 'Name your sprint and choose its window on the timeline.'}
                    </p>
                </div>
            }
            open={open}
            onCancel={handleClose}
            footer={null}
            width={500}
            centered
            destroyOnHidden
            maskClosable={!submitting && !detailLoading}
            closable={!submitting && !detailLoading}
        >
            <div className={styles.formWrap}>
                {detailLoading && mode === 'edit' ? (
                    <div
                        className={styles.detailLoadingStripe}
                        role="status"
                        aria-live="polite"
                        aria-label="Loading sprint details"
                    />
                ) : null}
                <Form
                    form={form}
                    layout="vertical"
                    className={styles.form}
                    onFinish={handleSubmit}
                    requiredMark={false}
                >
                    <Form.Item
                        label="Sprint name"
                        name="title"
                        rules={[
                            {
                                required: true,
                                message: 'Please enter a sprint name',
                            },
                        ]}
                    >
                        <Input
                            size="small"
                            placeholder="e.g. Sprint 12 - Checkout"
                            autoFocus
                            allowClear
                            disabled={detailLoading && mode === 'edit'}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Sprint dates"
                        name="dateRange"
                        rules={[
                            {
                                required: true,
                                message: 'Please select start and end dates',
                            },
                        ]}
                    >
                        <RangePicker
                            size="small"
                            style={{ width: '100%' }}
                            format="MMM D, YYYY"
                            separator="→"
                            disabled={detailLoading && mode === 'edit'}
                        />
                    </Form.Item>

                    <div className={styles.modalFooter}>
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={submitting || detailLoading}
                                className={styles.cancelButton}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={
                                    submitting ||
                                    (detailLoading && mode === 'edit')
                                }
                                onClick={(e) => {
                                    if (
                                        submitting ||
                                        (detailLoading && mode === 'edit')
                                    ) {
                                        e.preventDefault()
                                    }
                                }}
                                className={`${styles.submitButton} ${
                                    submitting
                                        ? styles.submitButtonDisabled
                                        : ''
                                }`}
                            >
                                {mode === 'edit'
                                    ? submitting
                                        ? 'Saving...'
                                        : 'Save changes'
                                    : submitting
                                      ? 'Creating...'
                                      : 'Create sprint'}
                            </button>
                        </div>
                    </div>
                </Form>
            </div>
        </Modal>
    )
}

export default CreateSprintModal
